import React from "react";
import { Outlet } from "react-router-dom";
import NoAccess from "~/components/shared/no-access";
import { useGetOnboardingStatusQuery } from "~/redux/api/onboarding";
import { restApi } from "~/redux/restApi";

interface ModuleRouteProps {
  /**
   * The module key to check access for. Must match the `moduleKey` value
   * returned by the API (case-insensitive). E.g. "HSE", "Finance", "Assets".
   */
  moduleKey: string;
}

/**
 * Route-level guard for top-level module access.
 *
 * Checks both the onboarding locked-modules list and the user's own module
 * access settings. If the module is locked (subscription) or disabled
 * (role permission), renders <AccessRestriction /> instead of the outlet,
 * preventing direct URL navigation to disabled modules.
 *
 * This mirrors the `isModuleVisible()` logic in `ecosystem-dock.tsx` so that
 * the dock visibility and route access stay in sync.
 *
 * Usage in routes/index.tsx:
 *   <Route element={<ModuleRoute moduleKey="Finance" />}>
 *     <Route path="finance" element={<FinancePage />} />
 *     <Route path="finance/invoices" element={<InvoicesPage />} />
 *   </Route>
 */
export const ModuleRoute: React.FC<ModuleRouteProps> = ({ moduleKey }) => {
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const { data: myModuleAccess } = restApi.useGetMyModuleAccessQuery();

  // If data hasn't loaded yet, allow through — the dock handles the visual
  // state and a brief flash is acceptable. We do not block on loading.
  if (!onboardingStatus || !myModuleAccess) {
    return <Outlet />;
  }

  const isLocked = onboardingStatus.lockedModules?.some(
    (m) => m?.toLowerCase() === moduleKey.toLowerCase(),
  );

  const matchedAccess = Array.isArray(myModuleAccess)
    ? myModuleAccess.find(
        (ma) => ma?.moduleKey?.toLowerCase() === moduleKey.toLowerCase(),
      )
    : undefined;

  const isDisabled = matchedAccess ? !matchedAccess.isEnabled : false;

  if (isLocked || isDisabled) {
    return <NoAccess />;
  }

  return <Outlet />;
};

export default ModuleRoute;
